import React, { useState, useEffect } from "react";
import { Nav, Accordion, Button } from "react-bootstrap";
import "./drawer.css";
import Circle from "../Circle";
import Title from "../Title";
import { ConcerningLevelLibrary } from "app/containers/WebReportV1/helperFunctions";
import DrawerNavItem from "./DrawerNavItem";

const Drawer = ({
  hasProfileImage,
  title,
  metaDataContent,
  issueContent,
  closeDrawer,
  activeSection,
  foundSubsection,
  foundSection,
}) => {
  // Initializing all top-level sections to be open
  const initialActiveKeys = issueContent.map((section, index) => {
    return section?.subsections?.length > 0
      ? `accordion-${section.name}`
      : null;
  });

  const [sectionActiveKeys, setSectionActiveKeys] = useState(initialActiveKeys);
  const [subsectionActiveKeys, setSubsectionActiveKeys] = useState([]);

  // Function to toggle an accordion section
  const toggleAccordion = (key, isSubsection = false) => {
    if (isSubsection) {
      setSubsectionActiveKeys((prev) =>
        prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
      );
    } else {
      setSectionActiveKeys((prev) =>
        prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
      );
    }
  };

  const handlePrintReport = () => {
    window.print();
  };

  useEffect(() => {
    if (foundSection) {
      setSectionActiveKeys((prev) => {
        const sectionKey = `accordion-${foundSection?.name}`;
        return prev.includes(sectionKey) ? prev : [...prev, sectionKey];
      });
    }

    if (foundSubsection) {
      setSubsectionActiveKeys((prev) => {
        const subsectionKey = `accordion-${foundSubsection?.name}`;
        return prev.includes(subsectionKey) ? prev : [...prev, subsectionKey];
      });
    }
  }, [foundSection, foundSubsection]);

  const scrollToSection = (sectionName) => {
    const sectionElement = document.getElementById(
      `section-${sectionName?.toLowerCase().replace(/\s+/g, "-")}`
    );

    if (sectionElement) {
      sectionElement.scrollIntoView({ behavior: "smooth" });
    }
  };

  /* UI components */
  const metaDataNavItems = Object.keys(metaDataContent).map((item, index) => {
    if (!["Project Information", "Project Overview"].includes(item)) {
      return null;
    }

    return (
      <Nav key={item} className="metadata-nav-item">
        <div
          id={`nav-item-${item.toLowerCase()}`}
          className={`mx-2 w-100 ${
            activeSection === item.toLowerCase() ? "nav-item-active" : ""
          }`}
        >
          <Nav.Link href={`#${item.toLowerCase()}`} onClick={closeDrawer}>
            <div className="d-flex justify-content-between w-100">{item}</div>
          </Nav.Link>
        </div>
        <hr className="w-100 m-2" />
      </Nav>
    );
  });

  const issueContentArray = issueContent.map((section, index) => {
    const hasSubsections =
      section.subsections && section.subsections.length > 0;

    const issueTypeRecord = { hazard: 0, recommendation: 0, maintenance: 0 };
    if (!hasSubsections && section?.issues?.length > 0) {
      section.issues.forEach((issue) => {
        switch (issue.concern_level) {
          case 0:
            issueTypeRecord.maintenance++;
            break;
          case 1:
            issueTypeRecord.recommendation++;
            break;
          case 2:
            issueTypeRecord.hazard++;
            break;
          default:
            break;
        }
      });
    }

    const totalItems = Object.values(issueTypeRecord).reduce(
      (acc, curr) => acc + curr,
      0
    );

    const mostSeriousIssue =
      issueTypeRecord.hazard > 0
        ? "concern-hazard"
        : issueTypeRecord.recommendation > 0
        ? "concern-recommendation"
        : "concern-maintenance";

    return (
      <Accordion.Item eventKey={`accordion-${section.name}`} key={section.name}>
        <Accordion.Header
          className="section-accordion-header"
          onClick={() => scrollToSection(section.name)}
        >
          <div className="d-flex justify-content-between w-100">
            <div>{section.name}</div>
            {!hasSubsections ? (
              <Circle type={mostSeriousIssue} text={totalItems} />
            ) : null}
          </div>
        </Accordion.Header>
        {hasSubsections && (
          <Accordion.Body>
            {section.subsections.map((subsection) => {
              // show circle on subsection
              const issueTypeRecord = {
                hazard: 0,
                recommendation: 0,
                maintenance: 0,
              };
              if (subsection?.issues) {
                subsection.issues.forEach((issue) => {
                  const concernLevel = issue?.concern_level;
                  switch (concernLevel) {
                    case 0:
                      issueTypeRecord.maintenance++;
                      break;
                    case 1:
                      issueTypeRecord.recommendation++;
                      break;
                    case 2:
                      issueTypeRecord.hazard++;
                      break;
                    default:
                      break;
                  }
                });
              }
              // Count total issues, infos, and limits
              const totalItems =
                Object.values(issueTypeRecord).reduce(
                  (acc, curr) => acc + curr,
                  0
                ) +
                (subsection?.infos?.length || 0) +
                (subsection?.limits?.length || 0);

              const mostSeriousIssue =
                issueTypeRecord.hazard > 0
                  ? "concern-hazard"
                  : issueTypeRecord.recommendation > 0
                  ? "concern-recommendation"
                  : "concern-maintenance";

              return (
                <Accordion
                  key={subsection.name}
                  className="flex-column subsection-accordion-container"
                  activeKey={subsectionActiveKeys}
                >
                  <Accordion.Item eventKey={`accordion-${subsection.name}`}>
                    <Accordion.Header
                      onClick={() =>
                        toggleAccordion(`accordion-${subsection.name}`, true)
                      }
                    >
                      <div className="d-flex justify-content-between align-items-center w-100">
                        <div className="w-75">{subsection.name}</div>
                        <Circle type={mostSeriousIssue} text={totalItems} />
                      </div>
                    </Accordion.Header>
                    <Accordion.Body>
                      {(subsection.issues || []).map((issue) => (
                        <DrawerNavItem
                          key={issue.id}
                          id={issue.id}
                          name={issue.name}
                          order={issue.order}
                          activeSection={activeSection}
                          icon={
                            ConcerningLevelLibrary.issues[issue.concern_level]
                              ?.icon
                          }
                          closeDrawer={closeDrawer}
                        />
                      ))}
                      {(subsection.infos || []).map((info) => (
                        <DrawerNavItem
                          key={info.id}
                          id={info.id}
                          name={info.name}
                          order={info.order}
                          activeSection={activeSection}
                          icon={ConcerningLevelLibrary.infos[0]?.icon}
                          closeDrawer={closeDrawer}
                        />
                      ))}
                      {(subsection.limits || []).map((limit) => (
                        <DrawerNavItem
                          key={limit.id}
                          id={limit.id}
                          name={limit.name}
                          order={limit.order}
                          activeSection={activeSection}
                          icon={ConcerningLevelLibrary.limits[0]?.icon}
                          closeDrawer={closeDrawer}
                        />
                      ))}
                    </Accordion.Body>
                  </Accordion.Item>
                </Accordion>
              );
            })}
          </Accordion.Body>
        )}
      </Accordion.Item>
    );
  });

  return (
    <div className="drawer">
      <div className="drawer-content-container">
        <div
          className="drawer-title-container d-lg-block px-2"
          id="nav-item-section-cover-page"
        >
          <button
            className="close-drawer-button border-0"
            onClick={() => scrollToSection("cover page")}
          >
            <Title
              text={title}
              placedAt="drawer"
              hasProfileImage={hasProfileImage}
            />
          </button>
        </div>
        <div className="px-2 py-3">
          <Button className="print-report-button" onClick={handlePrintReport}>
            Print Report
          </Button>
        </div>
        <Nav className="metadata-nav-item">
          <div
            id="nav-item-challenge-summary"
            className={`mx-2 w-100`}
          >
            <Nav.Link
              as="button"
              onClick={() => {
                const el = document.getElementById("challenge-summary-section");
                if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                if (closeDrawer) closeDrawer();
              }}
              style={{ background: "none", border: "none", width: "100%", textAlign: "left" }}
            >
              <div className="d-flex justify-content-between w-100">Challenge Summary</div>
            </Nav.Link>
          </div>
          <hr className="w-100 m-2" />
        </Nav>
        {metaDataNavItems}
        <Accordion
          className="section-accordion-container"
          activeKey={sectionActiveKeys}
        >
          {issueContentArray}
        </Accordion>
      </div>
    </div>
  );
};

export default Drawer;
